import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  businessAccountRoom,
  connectRealtimeWhenAvailable,
  getAccessToken,
  getRealtimeUrl,
  parseAccessTokenClaims,
  subscribeRooms,
} from '@beatq/shared';
import { resolveBusinessAccountId } from '../../shell/businessContext.js';
import {
  bindBusinessSocketLive,
  businessOpsPollMs,
  parseBusinessRealtimeTicketEnvelope,
  type BusinessOpsSocket,
} from '../../shell/businessRealtime.js';
import { createRealtimeConnectionTicket } from './policiesApprovalsApi.js';

export { businessOpsPollMs };

const APPROVALS_POLL_DOWN_MS = 15_000;

/** Mint business realtime ticket + poll fallback for approval queue/detail. */
export function useBusinessApprovalsRealtime(enabled = true): { live: boolean } {
  const qc = useQueryClient();
  const qcRef = useRef(qc);
  qcRef.current = qc;
  const [live, setLive] = useState(false);

  const invalidate = () => {
    void qcRef.current.invalidateQueries({ queryKey: ['business', 'approvals'] });
    void qcRef.current.invalidateQueries({ queryKey: ['business', 'approval-detail'] });
  };
  const invalidateRef = useRef(invalidate);
  invalidateRef.current = invalidate;

  useEffect(() => {
    if (!enabled) {
      setLive(false);
      return;
    }

    const accountId = resolveBusinessAccountId(
      parseAccessTokenClaims(getAccessToken()).bac ?? null,
    );
    if (!accountId) {
      setLive(false);
      return;
    }

    let cancelled = false;
    let socket: BusinessOpsSocket | null = null;
    let unbindLive: (() => void) | undefined;

    const onAny = () => invalidateRef.current();

    void (async () => {
      let minted: ReturnType<typeof parseBusinessRealtimeTicketEnvelope> = null;
      try {
        const envelope = await createRealtimeConnectionTicket({
          purpose: 'business-approvals',
          businessAccountId: accountId,
        });
        minted = parseBusinessRealtimeTicketEnvelope(envelope);
      } catch {
        /* JWT handshake is valid when ticket mint fails */
      }
      if (cancelled) return;

      try {
        socket = await connectRealtimeWhenAvailable({
          surface: 'business',
          accessToken: getAccessToken() ?? undefined,
          ...(minted?.ticket ? { ticket: minted.ticket } : {}),
          url: minted?.websocketUrl ?? getRealtimeUrl(),
        });
        if (cancelled) return;
        if (!socket) {
          setLive(false);
          return;
        }
        await subscribeRooms(socket, [businessAccountRoom(accountId)]);
        if (cancelled) return;
        unbindLive = bindBusinessSocketLive(socket, (next) => {
          if (!cancelled) setLive(next);
        });
        socket.onAny(onAny);
      } catch {
        if (!cancelled) setLive(false);
      }
    })();

    return () => {
      cancelled = true;
      setLive(false);
      unbindLive?.();
      if (socket) socket.offAny(onAny);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const accountId = resolveBusinessAccountId(
      parseAccessTokenClaims(getAccessToken()).bac ?? null,
    );
    if (!accountId) return;
    const poll = window.setInterval(
      () => invalidateRef.current(),
      businessOpsPollMs(live, APPROVALS_POLL_DOWN_MS),
    );
    return () => window.clearInterval(poll);
  }, [enabled, live]);

  return { live };
}
